// Keyboard shortcuts for the whole board.

import { useEffect } from "react";
import { addWidget, bringToFront, copyItems, cycleBoard, deleteItems, duplicateItems, nudge, pasteItems, redo, sendToBack, switchBoard, toggleLock, undo } from "../state/actions";
import { boardForKey } from "../state/boardKeys";
import { boardItems, currentBoard, get, set } from "../state/store";
import { kindOf } from "../../shared/boardKinds";
import { camera } from "../canvas/camera";
import { bounds, itemRect } from "../canvas/geometry";
import type { Item } from "../../shared/types";
import { isTyping } from "../lib/util";
import { toggleChat } from "../agent/store";
import { exitFullscreen, layerOpen, toggleFullscreen } from "./Fullscreen";
import { liveMap, withMap } from "../boards/mindmap/live";
import { openThemePicker } from "./ThemePicker";
import { nextBackground } from "../state/themes";
import { openOverview } from "./Panels";

/** numbers: listed while the number keys are on (Settings, Keyboard). */
export const SHORTCUTS: { keys: string; what: string; group: string; numbers?: boolean }[] = [
  { group: "Canvas", keys: "Scroll", what: "Move around" },
  { group: "Canvas", keys: "Ctrl or ⌘ + scroll, or pinch", what: "Zoom" },
  { group: "Canvas", keys: "Drag empty space", what: "Move around" },
  { group: "Canvas", keys: "Shift + drag", what: "Select several" },
  { group: "Canvas", keys: "Space + drag", what: "Move around from anywhere" },
  { group: "Canvas", keys: "Arrows", what: "Move around, with nothing selected (shift for faster)" },
  { group: "Canvas", keys: "⇧1", what: "See everything" },
  { group: "Canvas", keys: "⇧2", what: "Zoom to the selection" },
  { group: "Canvas", keys: "0", what: "Zoom to 100%" },
  { group: "Canvas", keys: "+ / −", what: "Zoom in or out" },
  { group: "Canvas", keys: "Double-click", what: "Add a widget there" },
  { group: "Tools", keys: "V", what: "Select" },
  { group: "Tools", keys: "A", what: "Add a widget" },
  { group: "Tools", keys: "N", what: "Sticky note" },
  { group: "Tools", keys: "P", what: "Draw" },
  { group: "Tools", keys: "R / O / L", what: "Box, circle, arrow" },
  { group: "Tools", keys: "T", what: "Label" },
  { group: "Tools", keys: "E", what: "Eraser" },
  { group: "Tools", keys: "D", what: "Data and sources" },
  { group: "Edit", keys: "⌘Z / ⇧⌘Z", what: "Undo, redo" },
  { group: "Edit", keys: "⌘D", what: "Duplicate" },
  { group: "Edit", keys: "⌘C / ⌘V", what: "Copy, paste" },
  { group: "Edit", keys: "⌫", what: "Delete" },
  { group: "Edit", keys: "Arrows", what: "Nudge the selection (shift for 10)" },
  { group: "Edit", keys: "] / [", what: "Bring forward, send back" },
  { group: "Edit", keys: "⇧L", what: "Lock or unlock" },
  { group: "Edit", keys: "Enter", what: "Edit the selected text" },
  { group: "Edit", keys: "⌘A", what: "Select everything" },
  { group: "Sheet", keys: "F2", what: "Edit the selected cell" },
  { group: "Sheet", keys: "⇧F2", what: "Write a note on the cell" },
  { group: "Sheet", keys: "⌘F", what: "Find in the sheet" },
  { group: "Sheet", keys: "⇧Space", what: "Select the row" },
  { group: "Sheet", keys: "⌘Space", what: "Select the column" },
  { group: "Board", keys: "⌘K or /", what: "Command bar" },
  { group: "Board", keys: "⌘J", what: "Open the chat with your agent" },
  { group: "Board", keys: "G", what: "All boards" },
  { group: "Board", keys: "1 to 9", what: "Open a board by its number in the list", numbers: true },
  { group: "Board", keys: "⌘[ / ⌘]", what: "Previous or next board" },
  { group: "Board", keys: "⇧T", what: "Try on themes" },
  { group: "Board", keys: "B", what: "Next background, on a theme with pictures" },
  { group: "Board", keys: "F", what: "Full screen: a doc, sheet, deck or files fills the tab, other boards the screen (Esc to leave)" },
  { group: "Board", keys: "⇧F", what: "Hide the buttons" },
  { group: "Board", keys: "⌘,", what: "Settings" },
  { group: "Board", keys: "?", what: "This list" },
  { group: "Board", keys: "Esc", what: "Close, deselect" },
];

/** The keys that act on a canvas alone: its items, its drawing tools and its camera. */
export function canvasOnly(e: Pick<KeyboardEvent, "key" | "shiftKey">, k: string, mod: boolean): boolean {
  if (mod) return k === "d" || k === "c" || k === "v" || k === "a";
  if (e.key === "Backspace" || e.key === "Delete" || e.key === "Enter" || e.key.startsWith("Arrow")) return true;
  if (e.shiftKey && (e.key === "!" || e.key === "@" || k === "l")) return true;
  if (["]", "[", "0", "=", "+", "-", "_"].includes(e.key)) return true;
  return !e.shiftKey && ["v", "p", "r", "o", "l", "t", "e", "n"].includes(k);
}

/** Screen pixels a second an arrow held moves the board, how much faster with Shift, and the least time a tap moves it for. */
const PAN_SPEED = 900;
const PAN_FAST = 2.5;
const PAN_TAP = 140;

/** The arrows held with nothing selected glide around the board, two at once on a diagonal, for as long as
 * they are held. A frame loop rather than the key's repeats, which start late and step. */
function arrowPan() {
  // each arrow down, and when it may stop: never while held, a moment after a tap
  const down = new Map<string, { at: number; until: number }>();
  let fast = false;
  let frame = 0;
  let last = 0;
  let began = 0;
  const step = (now: number) => {
    for (const [k, d] of down) if (d.until <= now) down.delete(k);
    const dx = (down.has("ArrowLeft") ? 1 : 0) - (down.has("ArrowRight") ? 1 : 0);
    const dy = (down.has("ArrowUp") ? 1 : 0) - (down.has("ArrowDown") ? 1 : 0);
    // a tile picked or a field focused while held takes the arrows back
    if ((!dx && !dy) || get().selection.length || isTyping({ target: document.activeElement })) return stop();
    const dt = Math.min(0.05, Math.max(0, now - last) / 1000);
    last = now;
    // it eases in over its first moments, so a tap moves a little and a hold travels
    const v = (PAN_SPEED * (fast ? PAN_FAST : 1) * dt * Math.min(1, 0.3 + (now - began) / 350)) / Math.hypot(dx, dy);
    camera.panBy(dx * v, dy * v);
    frame = requestAnimationFrame(step);
  };
  const stop = () => {
    down.clear();
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };
  return {
    press(e: KeyboardEvent) {
      const now = performance.now();
      if (!down.has(e.key)) down.set(e.key, { at: now, until: Infinity });
      fast = e.shiftKey;
      if (frame) return;
      last = began = now;
      frame = requestAnimationFrame(step);
    },
    release(e: KeyboardEvent) {
      fast = e.shiftKey;
      const d = down.get(e.key);
      if (d) d.until = d.at + PAN_TAP;
    },
    stop,
  };
}

/** Keys that go on working when held into a field: the arrows, Tab and the modifiers. */
const ROAMING = /^(Arrow|Tab$|Shift$|Control$|Alt$|Meta$)/;

export function useShortcuts() {
  useEffect(() => {
    const pan = arrowPan();
    // The key whose press began outside a field, while it is held. The shortcut it ran may have put
    // the focus in a field (the gallery's search, the theme strip's, a note opened to edit), and its
    // repeats are not typed there.
    let held: string | null = null;
    const onHold = (e: KeyboardEvent) => {
      if (!e.repeat) {
        held = e.code && !isTyping(e) && !ROAMING.test(e.key) ? e.code : null;
        return;
      }
      if (e.code === held && isTyping(e)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    // Whether something was open when Esc went down: it closes it, and it is not also the key that leaves full
    // screen. Read first of all, since a layer may close itself on this very key before the shortcut sees it.
    let escHeld = false;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") escHeld = get().presenting && layerOpen();
    };
    const onKey = (e: KeyboardEvent) => {
      // A held key repeats. A shortcut acts on its first press only, but for the ones made to be
      // held: undo and redo here, and further on the arrows nudging and + and - zooming.
      const once = (run: () => unknown) => {
        if (!e.repeat) run();
      };
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j" && isTyping(e)) {
        e.preventDefault();
        once(toggleChat);
        return;
      }
      if (isTyping(e)) return;
      const s = get();
      const mod = e.metaKey || e.ctrlKey;
      const k = e.key.toLowerCase();
      const sel = s.selection;
      if (mod && k === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (mod && k === "j") {
        e.preventDefault();
        once(toggleChat);
        return;
      }
      if (mod && k === "y") {
        e.preventDefault();
        redo();
        return;
      }
      // Settings, as most apps open theirs (Features 4, Tidy)
      if (mod && e.key === ",") {
        e.preventDefault();
        set({ panel: "settings", panelArg: null });
        return;
      }
      // A mind map's selected idea takes its keys first (Tab, Enter, Space, Delete, the arrows, Esc)
      const kind = kindOf(currentBoard(s));
      if (kind === "mindmap" && !mod && liveMap(s.boardId)?.key(e)) return;
      // A typed board (a doc) has no canvas: its widgets, drawing tools, zoom and selection keys do nothing
      // there, and the browser keeps its own copy, paste and select all. Undo, the chat, the boards, full
      // screen, the theme strip, the command bar and the panels work as everywhere. A mind map is drawn on a
      // canvas, with widgets beside it: only the drawing tools stay off there.
      if (kind === "mindmap" ? !mod && !e.shiftKey && ["p", "r", "o", "l", "t", "e"].includes(k) : kind !== "canvas" && canvasOnly(e, k, mod)) return;
      if (mod && k === "d" && sel.length) {
        e.preventDefault();
        once(() => duplicateItems(sel));
        return;
      }
      if (mod && k === "c" && sel.length) {
        once(() => copyItems(sel));
        return;
      }
      if (mod && k === "v") {
        // held, it pastes once, and the browser's own paste stays off for the rest
        if (e.repeat) return void e.preventDefault();
        // a copied board item comes back as JSON from the system clipboard
        if (navigator.clipboard?.readText && window.isSecureContext) {
          e.preventDefault();
          navigator.clipboard.readText().then(
            (text) => {
              try {
                const data = JSON.parse(text) as { contextBoard?: number; items?: Item[] };
                if (data.contextBoard && Array.isArray(data.items)) return pasteItems(data.items);
              } catch {
                /* not ours */
              }
              pasteItems();
            },
            () => pasteItems(),
          );
        } else pasteItems();
        return;
      }
      if (mod && k === "a") {
        e.preventDefault();
        once(() => set({ selection: boardItems(s).map((i) => i.id) }));
        return;
      }
      if (mod && (e.key === "[" || e.key === "]")) {
        e.preventDefault();
        once(() => cycleBoard(e.key === "[" ? -1 : 1));
        return;
      }
      if (mod) return;
      // past its first press a held key does nothing here, but for the arrows nudging and + and - zooming
      if (e.repeat && !e.key.startsWith("Arrow") && !["=", "+", "-", "_"].includes(e.key)) return;
      // 1 to 9 open the boards list's boards, numbered as the drop-down shows them, on every kind of board
      // (Features 2, 21 Oct); a number with no board, or with something open over the board, does nothing
      if (/^[1-9]$/.test(e.key)) {
        const to = boardForKey(s, e);
        if (!to) return;
        e.preventDefault();
        if (to.id !== s.boardId) switchBoard(to.id);
        return;
      }
      // In full screen Esc closes what was opened there first, as on the board, then leaves, wherever the
      // browser has not done it already
      if (e.key === "Escape" && s.presenting) {
        if (escHeld) return;
        const settings = s.selection.length > 0 && (s.inspect || s.selection.length > 1 || s.items[s.selection[0]]?.kind !== "widget");
        if (s.panel) set({ panel: null, panelArg: null });
        else if (settings) set({ selection: [], inspect: false });
        else exitFullscreen();
        return;
      }
      // and brings hidden buttons back, the way out anyone tries first
      if (e.key === "Escape" && s.focusMode) {
        set({ focusMode: false });
        return;
      }
      if (e.key === "Escape") {
        if (s.panel) set({ panel: null, panelArg: null });
        else if (s.editing) set({ editing: null });
        else if (s.tool !== "select") set({ tool: "select" });
        else set({ selection: [] });
        return;
      }
      if ((e.key === "Backspace" || e.key === "Delete") && sel.length) {
        e.preventDefault();
        deleteItems(sel);
        return;
      }
      if (e.key.startsWith("Arrow") && sel.length) {
        e.preventDefault();
        const d = e.shiftKey ? 10 : 1;
        nudge(sel, e.key === "ArrowLeft" ? -d : e.key === "ArrowRight" ? d : 0, e.key === "ArrowUp" ? -d : e.key === "ArrowDown" ? d : 0);
        return;
      }
      if (e.key.startsWith("Arrow") && !e.altKey) {
        e.preventDefault();
        pan.press(e);
        return;
      }
      if (e.key === "Enter" && sel.length === 1) {
        const it = s.items[sel[0]];
        if (it && ((it.kind === "widget" && ["notes", "heading", "sticky"].includes(it.type)) || (it.kind === "ink" && it.ink.t === "text"))) {
          e.preventDefault();
          set({ editing: it.id });
        }
        return;
      }
      if (e.shiftKey && e.key === "!") {
        camera.fit(bounds(withMap(boardItems(s).map(itemRect))), { maxZ: 1 });
        return;
      }
      if (e.shiftKey && e.key === "@") {
        const b = bounds(sel.map((id) => s.items[id]).filter(Boolean).map(itemRect));
        if (b) camera.fit(b, { maxZ: 1.4, pad: 100 });
        return;
      }
      if (e.shiftKey && k === "t") {
        // the strip of themes, tried on as you move through it; the T must not land in its search box
        e.preventDefault();
        if (s.themePicker) set({ themePicker: false, themePreview: null });
        else openThemePicker();
        return;
      }
      if (e.shiftKey && k === "l" && sel.length) {
        toggleLock(sel);
        return;
      }
      if (e.shiftKey && k === "f") {
        // the controls hide, the window stays as it is
        if (s.presenting) exitFullscreen();
        else set({ focusMode: !s.focusMode });
        return;
      }
      // A key that would close the dialog or panel open, or put another in its place (?, A, D, G, F, Shift T),
      // leaves one holding words someone typed and has not saved: the store's guard turns it away (store.ts).
      if (e.key === "?") {
        set({ panel: "shortcuts" });
        return;
      }
      if (e.key === "]" && sel.length) return bringToFront(sel);
      if (e.key === "[" && sel.length) return sendToBack(sel);
      if (e.key === "0") return camera.zoomTo(1);
      if (e.key === "=" || e.key === "+") return camera.zoomStep(1.25);
      if (e.key === "-" || e.key === "_") return camera.zoomStep(1 / 1.25);
      if (e.shiftKey || e.altKey) return;
      const tools: Record<string, typeof s.tool> = { v: "select", p: "pen", r: "rect", o: "ellipse", l: "arrow", t: "text", e: "eraser" };
      if (tools[k]) {
        set({ tool: tools[k], editing: null, ...(k !== "v" ? { selection: [] } : {}) });
        return;
      }
      if (k === "a") return set({ panel: s.panel === "gallery" ? null : "gallery", panelArg: null });
      if (k === "n") return void addWidget("sticky");
      if (k === "d") return set({ panel: s.panel === "data" ? null : "data", panelArg: null });
      if (k === "f") return toggleFullscreen();
      if (k === "g") return openOverview();
      if (k === "b") return void nextBackground();
    };
    // on the way down, so a repeat is stopped before it reaches the field
    window.addEventListener("keydown", onEsc, true);
    window.addEventListener("keydown", onHold, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", pan.release);
    window.addEventListener("blur", pan.stop);
    return () => {
      window.removeEventListener("keydown", onEsc, true);
      window.removeEventListener("keydown", onHold, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", pan.release);
      window.removeEventListener("blur", pan.stop);
      pan.stop();
    };
  }, []);
}
